import React from 'react';
import {
  Card, CardContent, CardMedia, Button, IconButton, Tooltip, Chip,
  Typography, Dialog, DialogActions, DialogContent, DialogTitle, Box, Stack, Autocomplete, TextField
} from '@mui/material';
import {
  Delete, Settings, Block, Check, Public, Person, Timeline, Add, QrCode, Save, Flag
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import * as ETVConstants from '../ETVConstants';
import CropOriginalIcon from '@mui/icons-material/CropOriginal';
import QRCodeOverlay from '../tools/QRCodeOverlay.js';
import TimeDateSettings from '../tools/TimeDateSettings.js';
import RestrictionSettings from '../tools/RestrictionSettings.js';

class ContentLibraryItem extends React.Component {
  state = {
    window: '',
    new_owner_id: -1,
    qr_code_url: '',
    qr_code_alignmnet:0,
    user_name: ''
  };

  componentDidMount() {
    this.setState({
      new_owner_id: this.props.user_id,
      user_name: localStorage.getItem('etv.user_name') || ''
    });
  }

  // ---------- helpers ----------
  isMovie = (f = '') => /\.mp(g|4|eg)$/i.test(f);
  isHTML  = (f = '') => /\.html?$/i.test(f);
  isMusic = (f = '') => /\.(mp3|m4a)$/i.test(f);
  isPDF   = (f = '') => /\.pdf$/i.test(f);

  makeImageUrlRelative(url) {
    if (!url) return url;
    const idx = url.indexOf('/contents');
    return idx >= 0 ? url.substring(idx) : url;
  }

  getThumbnail() {
    const title = (this.props.item.filename || '').toLowerCase();
    let imageUrl = this.props.item.thumbnail;
    if (ETVConstants.ON_PREMISE_VERSION || !imageUrl) {
      if (this.isMovie(title)) imageUrl = '/image_assets/content/movie_placeholder.png';
      else if (this.isHTML(title))  imageUrl = '/image_assets/content/html_placeholder.png';
      else if (this.isMusic(title)) imageUrl = '/image_assets/content/music_placeholder.png';
      else if (this.isPDF(title))   imageUrl = '/image_assets/content/pdf_placeholder.png';
    }
    return imageUrl;
  }

  getChipColor(category) {
    if (category === 'image') return '#199139';
    if (category === 'movie') return '#1d10a3';
    if (category === 'pdf')   return '#fc2116';
    if (category === 'html')  return '#c28f04';
    return '#e82764';
  }

  ownerIsChild = (ownerID) => (this.props.children || []).includes(ownerID); // NOTE: consider renaming this prop to avoid confusion with React children
  userCanContributeMarketplace = () => (this.props.user_roles || []).includes('admin.content.contribute_marketplace');

  // ---------- actions ----------
  addToChannel = (contentID, channel, deviceID, groupID) => {
    let url = `${ETVConstants.getServerBase()}/playlist?action=add_to_playlist&channel=${channel}&device_id=${deviceID}&user_id=${this.props.user_id}&content_id=${contentID}`;
    if (groupID >= 0) {
      url = `${ETVConstants.getServerBase()}/playlist?action=add_to_group_playlist&channel=${channel}&group_id=${groupID}&user_id=${this.props.user_id}&content_id=${contentID}`;
    }
    ETVConstants.sendCMDToServer(url);
    this.props.addedToChannelF();
  };

  showWindow = (id, show) => this.setState({ window: show ? id : '' });
  showChangeOwner       = () => this.showWindow('show_change_owner_div', true);
  showQRCode            = () => this.showWindow('qr_code_div', true);
  showRestrictions      = () => this.showWindow('restrictions_div', true);
  showTimeDateSettings  = () => this.showWindow('time_date_div', true);

  saveNewOwner = () => {
    const url = `${ETVConstants.getServerBase()}/content_library?action=update_owner&user_id=${this.state.new_owner_id}&content_id=${this.props.id}`;
    ETVConstants.sendCMDToServerListener(url, () => this.showWindow('show_change_owner_div', false));
  };

  updateQRCode = (qr, alignment) => this.setState({ qr_code_url: qr, qr_code_alignment:alignment });

  saveQRCode = () => {
    const url = `${ETVConstants.getServerBase()}/content_library?action=set_qr_code&content_id=${this.props.id}&qr_code_alignment=${this.state.qr_code_alignment}&qr_code=${encodeURIComponent(this.state.qr_code_url)}`;
    //console.log("---->updateQR: "+url);
    ETVConstants.sendCMDToServerListener(url, () => this.showWindow('qr_code_div', false));
  };

  deleteQRCode = () => {
    const url = `${ETVConstants.getServerBase()}/content_library?action=set_qr_code&content_id=${this.props.id}&qr_code=`;
    ETVConstants.sendCMDToServerListener(url, () => this.showWindow('qr_code_div', false));
  };

  isValidQRUrl(content) {
    if (typeof content !== 'string' || content.trim().length === 0) return false;
    if (content.startsWith('BEGIN:VCARD') || content.startsWith('WIFI:') || content.startsWith('MATMSG:')) return true;
    try {
      const parsed = new URL(content);
      return ['http:', 'https:', 'sms:', 'tel:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  }

  getAvailableUserList() {
    const { available_users, user_id } = this.props;
    const { new_owner_id, user_name } = this.state;

    if (!available_users || available_users.length === 0) return null;

    // Merge current user + available users (avoid duplicates)
    const options = [
      { user_id, user_name },
      ...available_users.filter(u => u.user_id !== user_id),
    ];

    const selectedUser =
      options.find(u => u.user_id === new_owner_id) || null;

    return (
      <Box mt={2}>
        <Autocomplete
          size="small"
          options={options}
          value={selectedUser}
          getOptionLabel={(option) => option.user_name || ''}
          isOptionEqualToValue={(option, value) =>
            option.user_id === value.user_id
          }
          onChange={(event, newValue) => {
            this.setState({
              new_owner_id: newValue ? newValue.user_id : null,
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={ETVConstants.trans('change_owner')}
              variant="outlined"
              sx={{ minWidth: 220 }}
            />
          )}
        />
      </Box>
    );
  }

  formatAspectRatio(r) {
    if (!r || typeof r !== 'number' || r=="-1") return '';

    // --- Common well-known ratios ---
    const known = [
      { ratio: 16/9,  label: '16:9' },
      { ratio: 9/16,  label: '9:16' },
      { ratio: 4/3,   label: '4:3' },
      { ratio: 3/4,   label: '3:4' },
      { ratio: 5/4,  label: '5:4' },
      { ratio: 4/5,  label: '4:5' },
      { ratio: 1,     label: '1:1' },
      { ratio: 21/9,  label: '21:9' },
      { ratio: 9/21,  label: '9:21' }
    ];

    for (const k of known) {
      if (Math.abs(r - k.ratio) < 0.01) return k.label;   // tolerance
    }

    const precision = 100; // increase if you want more accuracy
    let numerator   = Math.round(r * precision);
    let denominator = precision;

    const gcd = (a, b) => b === 0 ? a : gcd(b, a % b);
    const d = gcd(numerator, denominator);

    numerator   /= d;
    denominator /= d;

    return `${numerator}:${denominator}`;
  }




  render() {
    // --- Uniform card & thumbnail ---
    const CARD_WIDTH   = 220;
    const CARD_HEIGHT  = 340;        // keeps grid rows aligned
    const THUMB_RATIO  = 4 / 3;      // change to 16/9 if you prefer
    const thumbPad     = `${100 / THUMB_RATIO}%`; // 75% for 4:3

    // Data
    const rawTitle = this.props.item.filename || '';
    const title    = rawTitle.includes('_') ? rawTitle.substring(rawTitle.indexOf('_') + 1) : rawTitle;
    const desc     = this.props.item.description || '';
    const active   = !!this.props.item.active;
    const isPublic = !!this.props.item.public;
    const canEdit  = this.props.item.owner_id === this.props.user_id || this.ownerIsChild(this.props.item.owner_id);
    const category = this.props.item.content_category || '';
    const contentRatio = this.formatAspectRatio(this.props.item.content_ratio);

    const imgSrc      = this.getThumbnail();
    const resolvedImg = ETVConstants.ON_PREMISE_VERSION ? this.makeImageUrlRelative(imgSrc) : imgSrc;

    const qrOpen   = this.state.window === 'qr_code_div';
    const ownOpen  = this.state.window === 'show_change_owner_div';
    const resOpen  = this.state.window === 'restrictions_div';
    const timeOpen = this.state.window === 'time_date_div';

    return (
      <Card
        sx={{
          width: CARD_WIDTH,
          height: CARD_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'transform .15s ease, box-shadow .15s ease',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }
        }}
      >
        {/* Thumbnail with fixed aspect ratio */}
        <Box sx={{ position: 'relative', width: '100%', pt: thumbPad, bgcolor: 'action.hover' }}>
          <CardMedia
            component="img"
            loading="lazy"
            src={resolvedImg}
            alt={title}
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'center'
            }}
          />
          <Chip
            size="small"
            label={category ? ETVConstants.trans(category) : ETVConstants.trans('uncategorized')}
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              color: category ? 'common.white' : 'common.white',
              bgcolor: category ? this.getChipColor(category) : '#696865',
              border: category ? 0 : '1px solid',
              borderColor: 'divider'
            }}
          />
          {!!contentRatio && (
            <Chip
              size="small"
              icon={<CropOriginalIcon sx={{ fontSize: 14 }} />}
              label={contentRatio}
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.72)',
                color: 'common.white',
                height: 22,
                '& .MuiChip-label': {
                  px: 1
                },
                '& .MuiChip-icon': {
                  color: 'inherit',
                  ml: 0.25
                }
              }}
            />
          )}          
        </Box>

        {/* Body */}
        <CardContent sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
          {!this.props.marketplace && (
            <Typography
              variant="subtitle2"
              sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              title={title}
            >
              {title}
            </Typography>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '2.6em'
            }}
            title={desc}
          >
            {desc}
          </Typography>

          {/* Actions */}
          {this.props.addedToChannelF ? (
            <Box sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                size="small"
                color="success"
                startIcon={<Add />}
                onClick={() =>
                  this.addToChannel(this.props.id, this.props.channel, this.props.device_id, this.props.group_id)
                }
                fullWidth
              >
                {ETVConstants.trans('add')}
              </Button>
            </Box>
          ) : (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 'auto', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
            >
              {/* Active toggle */}
              {!active ? (
                <Tooltip title={ETVConstants.trans('set_active')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canEdit}
                      sx={{ color: '#ff3b30' }}
                      onClick={() => this.props.activeF(this.props.id, !this.props.active)}
                    >
                      <Block />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <Tooltip title={ETVConstants.trans('set_inactive')}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!canEdit}
                      sx={{ color: '#00ad0e' }}
                      onClick={() => this.props.activeF(this.props.id, !this.props.active)}
                    >
                      <Check />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Marketplace publish toggle */}
              {this.userCanContributeMarketplace() && !ETVConstants.ON_PREMISE_VERSION && (
                isPublic ? (
                  <Tooltip title={ETVConstants.trans('unpublish_from_marketplace')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canEdit}
                        color="primary"
                        onClick={() => this.props.publicF(this.props.id, !isPublic)}
                      >
                        <Public />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={ETVConstants.trans('publish_to_marketplace')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={!canEdit}
                        color="secondary"
                        onClick={() => this.props.publicF(this.props.id, !isPublic)}
                      >
                        <Block />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              )}

              <Tooltip title={ETVConstants.trans('settings')}>
                <IconButton size="small" color="primary" onClick={() => this.props.showSettingsF(this.props.id)}>
                  <Settings />
                </IconButton>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('hit_report')}>
                <IconButton size="small" color="primary" onClick={() => this.props.showHitReportF(this.props.id)}>
                  <Timeline />
                </IconButton>
              </Tooltip>

              {this.props.available_users && this.props.available_users.length >= 1 && (
                <Tooltip title={ETVConstants.trans('change_owner')}>
                  <IconButton size="small" color="primary" onClick={this.showChangeOwner}>
                    <Person />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={ETVConstants.trans('time_date_settings')}>
                <span>
                  <IconButton size="small" color="primary" onClick={this.showTimeDateSettings}>
                    <AccessTimeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('qr_code_overlay')}>
                <span>
                  <IconButton size="small" color="primary" onClick={this.showQRCode}>
                    <QrCode />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('restrictions')}>
                <span>
                  <IconButton size="small" color="primary" onClick={this.showRestrictions}>
                    <Flag />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={ETVConstants.trans('delete')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!canEdit}
                    color={canEdit ? 'error' : 'primary'}
                    onClick={() => this.props.deleteF(this.props.id)}
                  >
                    <Delete />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
        </CardContent>

        {/* Dialogs — children gated to avoid rendering undefined components */}
        <Dialog open={ownOpen} maxWidth="xs" onClose={() => this.showWindow('', false)}>
          <DialogTitle>{ETVConstants.trans('change_owner')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{ETVConstants.trans('change_owner_help')}</Typography>
            {this.getAvailableUserList()}
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => this.showWindow('', false)}>{ETVConstants.trans('cancel')}</Button>
            <Button size="small" variant="contained" color="success" onClick={this.saveNewOwner}>
              {ETVConstants.trans('save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={qrOpen} maxWidth="sm" fullWidth onClose={() => this.showWindow('', false)}>
          <DialogTitle>{ETVConstants.trans("qr_code_overlay")}</DialogTitle>
          <DialogContent dividers>
            {qrOpen && QRCodeOverlay && (
              this.isPDF(this.props.item.filename)
                ? <QRCodeOverlay disabled={!canEdit} content_id={this.props.item.content_id} download_url={this.props.item.url} onQRUrlChange={this.updateQRCode} />
                : <QRCodeOverlay disabled={!canEdit} content_id={this.props.item.content_id} onQRUrlChange={this.updateQRCode} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>{ETVConstants.trans('cancel')}</Button>
            <Button onClick={this.deleteQRCode} disabled={!this.state.qr_code_url} startIcon={<Delete />} color="error" variant="contained">
              {ETVConstants.trans('delete')}
            </Button>
            <Button
              onClick={this.saveQRCode}
              disabled={!this.state.qr_code_url || !this.isValidQRUrl(this.state.qr_code_url)}
              startIcon={<Save />} color="success" variant="contained"
            >
              {ETVConstants.trans('save')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={resOpen} maxWidth="sm" fullWidth onClose={() => this.showWindow('', false)}>
          <DialogContent>
            {resOpen && RestrictionSettings && (
              <RestrictionSettings disabled={!canEdit} content_id={this.props.item.content_id} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>{ETVConstants.trans('cancel')}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={timeOpen} maxWidth="sm" fullWidth onClose={() => this.showWindow('', false)}>
          <DialogContent>
            {timeOpen && TimeDateSettings && (
              <TimeDateSettings disabled={!canEdit} content_id={this.props.item.content_id} />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.showWindow('', false)}>{ETVConstants.trans('cancel')}</Button>
          </DialogActions>
        </Dialog>
      </Card>
    );
  }
}

export default ContentLibraryItem;
